import { Box } from "@mui/material";
import { LOOKUP_MASTERS, mapLookupValueToBookingStatusFilter, BOOKINGS_LIST_SORT_ARRIVAL_UI } from "api-client";
import {
  CommonDropdownGenerators,
  lookupItemsToFilterOptions,
  ListFiltersDrawer,
  useCustomersList,
  useLocationsList,
  type BookingRow,
  type BookingStatus,
  useLookup,
} from "design-components";
import { endOfWeek, format, formatDistanceToNow, isSameDay, startOfWeek } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { lookupService } from "../../../shared/services/lookup.service";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hook";
import {
  fetchBookings,
  setBookingsDateRange,
  setBookingsLocationFilters,
  setBookingsSort,
  setBookingsStatusFilters,
} from "../../../shared/store/bookings";
import {
  selectBookingsError,
  selectBookingsLoading,
  selectBookingsLocationFilterIds,
  selectBookingsRows,
  selectBookingsStatusFilterValues,
  selectBookingsTotal,
} from "../../../shared/store/bookings/bookings.selector";
import { fetchCarriersList } from "../../../shared/store/carriers";
import { selectCarriersListTotal } from "../../../shared/store/carriers/carriers.selector";
import aviroFullLogoUrl from "@design-system-assets/aviro360_logo_full.png";
import { DashboardBookingOverview } from "../components/dashboard-booking-overview.component";
import { DashboardBookingsPreview } from "../components/dashboard-bookings-preview.component";
import { DashboardCapacityCard } from "../components/dashboard-capacity-card.component";
import { DashboardPageHeader } from "../components/dashboard-page-header.component";
import { DashboardStatWidget } from "../components/dashboard-stat-widget.component";
import {
  DASHBOARD_CAPACITY_METRICS,
  DASHBOARD_MOCK_PREVIEW_ROWS,
  DASHBOARD_OVERVIEW_FALLBACK,
  DASHBOARD_PREVIEW_ROW_LIMIT,
  type DashboardOverviewCell,
  type DashboardStat,
} from "../types";

export const DashboardPage = () => {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectBookingsRows);
  const loading = useAppSelector(selectBookingsLoading);
  const error = useAppSelector(selectBookingsError);
  const totalBookings = useAppSelector(selectBookingsTotal);
  const locationFilterIds = useAppSelector(selectBookingsLocationFilterIds);
  const appliedStatusFilterValues = useAppSelector(selectBookingsStatusFilterValues);
  const carriersTotal = useAppSelector(selectCarriersListTotal);

  const { totalCount: customerTotal, loading: customersLoading } = useCustomersList({
    autoFetch: true,
  });
  const { locations: locationsList, loading: locationsLoading } = useLocationsList({
    autoFetch: true,
  });

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => [null, null]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<Record<string, string[]>>({});
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    dispatch(fetchCarriersList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      setBookingsSort({
        sortingField: BOOKINGS_LIST_SORT_ARRIVAL_UI,
        sortingOrder: "asc",
      }),
    );
    dispatch(
      setBookingsDateRange({
        dateFrom: null,
        dateTo: null,
      }),
    );
    dispatch(fetchBookings());
  }, [dispatch]);

  useEffect(() => {
    if (!loading && !error) {
      setLastUpdatedAt(new Date());
    }
  }, [loading, error, rows]);

  const locationOptions = useMemo(
    () =>
      CommonDropdownGenerators.locations(
        locationsList as Array<{ locationId: number | string; label: string }> | null,
      ),
    [locationsList],
  );

  const previewRows = useMemo(() => {
    if (loading || error) {
      return [];
    }
    const slice = rows.slice(0, DASHBOARD_PREVIEW_ROW_LIMIT);
    if (slice.length > 0) {
      return slice;
    }
    return DASHBOARD_MOCK_PREVIEW_ROWS;
  }, [loading, error, rows]);

  const overviewCells = useMemo(() => buildOverviewCells(rows), [rows]);

  const stats: DashboardStat[] = useMemo(
    () => [
      {
        title: "Total bookings",
        value: totalBookings,
        subtitle: "2 in the last hour",
      },
      {
        title: "Total customers",
        value: customersLoading ? 0 : customerTotal,
        subtitle: "1 onboarded today",
      },
      {
        title: "Total carriers",
        value: carriersTotal,
        subtitle: "1 onboarded this week",
      },
    ],
    [totalBookings, customerTotal, customersLoading, carriersTotal],
  );

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return "Data last updated —";
    const raw = formatDistanceToNow(lastUpdatedAt, { addSuffix: true });
    const compact = raw
      .replace(/^about\s+/i, "")
      .replace(/\s+minutes?\b/i, " min")
      .replace(/\s+seconds?\b/i, " sec");
    return `Data last updated ${compact}`;
  }, [lastUpdatedAt]);

  const handleDateRangeChange = useCallback(
    (next: [Date | null, Date | null]) => {
      setDateRange(next);
      const from = next[0] ? format(next[0], "yyyy-MM-dd") : null;
      const to = next[1] ? format(next[1], "yyyy-MM-dd") : null;
      if (from != null && to != null) {
        dispatch(setBookingsDateRange({ dateFrom: from, dateTo: to }));
        dispatch(fetchBookings());
      } else if (from == null && to == null) {
        dispatch(setBookingsDateRange({ dateFrom: null, dateTo: null }));
        dispatch(fetchBookings());
      }
    },
    [dispatch],
  );

  const handleLocationsChange = useCallback(
    (ids: string[]) => {
      dispatch(setBookingsLocationFilters(ids));
      dispatch(fetchBookings());
    },
    [dispatch],
  );

  const openFilterDrawer = useCallback(() => {
    setFilterDraft({ status: [...appliedStatusFilterValues] });
    setFilterDrawerOpen(true);
  }, [appliedStatusFilterValues]);

  const handleFilterDrawerClose = useCallback(() => setFilterDrawerOpen(false), []);

  const handleFilterApply = useCallback(
    (applied: Record<string, string[]>) => {
      dispatch(setBookingsStatusFilters(applied.status ?? []));
      dispatch(fetchBookings());
      setFilterDrawerOpen(false);
    },
    [dispatch],
  );

  const { items: bookingStatusLookupItems } = useLookup(lookupService, LOOKUP_MASTERS.bookingStatus);
  const bookingStatusFilterOptions = useMemo(
    () => lookupItemsToFilterOptions(bookingStatusLookupItems, mapLookupValueToBookingStatusFilter),
    [bookingStatusLookupItems],
  );

  const listFilterSections = useMemo(
    () => [{ id: "status", label: "Status", options: bookingStatusFilterOptions }],
    [bookingStatusFilterOptions],
  );

  return (
    <Box className="operator-dashboard">
      <DashboardPageHeader
        logoSrc={aviroFullLogoUrl}
        logoAlt="Aviro360"
        lastUpdatedLabel={lastUpdatedLabel}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        dateRangeLabel={dashboardWeekTriggerLabel(dateRange)}
        locationOptions={locationOptions}
        selectedLocationIds={locationFilterIds}
        onLocationsChange={handleLocationsChange}
        locationsLoading={locationsLoading}
        onFilterClick={openFilterDrawer}
      />

      <Box className="operator-dashboard__stats-grid">
        {stats.map((stat) => (
          <Box key={stat.title} className="operator-dashboard__stat-cell">
            <DashboardStatWidget {...stat} />
          </Box>
        ))}
      </Box>

      <Box className="operator-dashboard__columns">
        <Box className="operator-dashboard__col-left">
          <DashboardCapacityCard metrics={DASHBOARD_CAPACITY_METRICS} />
          <DashboardBookingOverview cells={overviewCells} />
        </Box>
        <Box className="operator-dashboard__col-right">
          <DashboardBookingsPreview rows={previewRows} loading={loading} error={error} />
        </Box>
      </Box>

      <ListFiltersDrawer
        open={filterDrawerOpen}
        onClose={handleFilterDrawerClose}
        title="Filters"
        subtitle="Select filters for bookings."
        sections={listFilterSections}
        value={filterDraft}
        onChange={setFilterDraft}
        onApply={handleFilterApply}
      />
    </Box>
  );
};

/** Figma: trigger shows “This week” (not the long range caption). */
function dashboardWeekTriggerLabel(range: [Date | null, Date | null]): string | undefined {
  const [a, b] = range;
  if (!a || !b) return undefined;
  const ws = startOfWeek(new Date(), { weekStartsOn: 1 });
  const we = endOfWeek(new Date(), { weekStartsOn: 1 });
  if (isSameDay(a, ws) && isSameDay(b, we)) {
    return "This week";
  }
  return undefined;
}

function buildOverviewCells(rows: BookingRow[]): DashboardOverviewCell[] {
  const counts: Record<BookingStatus, number> = {
    DRAFT: 0,
    UNASSIGNED: 0,
    PENDING: 0,
    CONFIRMED: 0,
    COMPLETE: 0,
    // Same intent as DASHBOARD_OVERVIEW_FALLBACK — counted but not surfaced
    // in the 4-cell overview grid.
    IN_PROGRESS: 0,
    NO_SHOW: 0,
    DELETED: 0,
  };
  for (const r of rows) {
    counts[r.status] += 1;
  }
  const hasData = rows.length > 0;
  const n = (s: BookingStatus) => (hasData ? counts[s] : DASHBOARD_OVERVIEW_FALLBACK[s]);

  return [
    { status: "UNASSIGNED", count: n("UNASSIGNED"), label: "Unassigned", icon: "warning" },
    { status: "PENDING", count: n("PENDING"), label: "Pending", icon: "info" },
    { status: "CONFIRMED", count: n("CONFIRMED"), label: "Confirmed", icon: "success" },
    { status: "COMPLETE", count: n("COMPLETE"), label: "Complete", icon: "neutral" },
  ];
}